import { yupResolver } from "@hookform/resolvers/yup";
import { OptionInterface } from "components/forms/inputs/dropdown.styles";
import { FunctionComponent } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocale, useLocales } from "stores/hooks/locale";
import * as yup from "yup";
import { User } from "./add-user-modal";
import FormButtons, { FormButtonInterface } from "components/forms/buttons";
import { CancelButton, SubmitButton } from "components/forms/buttons.utils";
import Form from "components/forms/form";
import Grid from "components/grid/grid";
import Row from "components/grid/row";
import Col from "components/grid/col";
import TextInput from "components/forms/inputs/text";
import Dropdown from "components/forms/inputs/dropdown";
import { StyledDeleteIcon } from "./add-user-modal.styles";
import Button from "components/button";
import { useTranslate } from "stores/hooks/translation";
import { faPlus, faTrash } from "components/icon-library";
import { getLanguageOptions } from "components/forms/inputs/dropdown.utils";
import Checkbox from "components/forms/inputs/checkbox";

interface UsersFormProps {
  defaults: User[];
  onNext: (users: User[], send_email: boolean) => Promise<void>;
  onPrevious: () => Promise<void>;
}

const UsersForm: FunctionComponent<UsersFormProps> = ({
  defaults,
  onNext,
  onPrevious,
}) => {
  const translate = useTranslate();
  const locale = useLocale();
  const locales = useLocales();

  // build validation schema based on fields
  const schema = yup.object({
    users: yup.array().of(
      yup.object({
        email: yup.string().validateEmail().validateRequired(),
        locale: yup.string().validateOneOf(locales),
      }),
    ),
    send_email: yup.boolean(),
  });

  // do default values
  const defaultValues = {
    users: [...defaults],
    send_email: false,
  };

  // form magic
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  // array magic
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "users",
  });

  const handleAdd = () => {
    append({ email: "", locale });
  };

  const handleSubmitUsers = async (formValues: any) => {
    onNext(formValues.users, formValues.send_email);
  };

  const localeOptions: OptionInterface[] = getLanguageOptions(
    locales,
    translate,
  );

  const buttons: FormButtonInterface[] = [
    { ...CancelButton, onClick: onPrevious },
    {
      ...SubmitButton,
      text: methods.getValues().send_email
        ? `Kirjoita sähköpostikutsu`
        : "Lisää ilmoitusvelvolliset",
    },
  ];

  return (
    <Form methods={methods} onSubmit={handleSubmitUsers}>
      <Grid>
        {fields.map((field, index) => {
          return (
            <Row key={field.id}>
              <Col>
                <TextInput
                  required
                  label="Sähköpostiosoite"
                  name={`users.${index}.email`}
                />
              </Col>
              <Col>
                <Dropdown
                  required
                  label="Kieli"
                  name={`users.${index}.locale`}
                  options={localeOptions}
                />
              </Col>
              <Col flex={0}>
                <StyledDeleteIcon
                  disabled={fields.length === 1}
                  icon={faTrash}
                  onClick={() => remove(index)}
                />
              </Col>
            </Row>
          );
        })}
        <Row>
          <Col>
            <Button
              preIcon={faPlus}
              type="tertiary"
              onClick={() => handleAdd()}
              disabled={fields.length >= 10}
            >
              Lisää uusi
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox
              name="send_email"
              label="Lähetä sähköpostikutsu lisätyille ilmoitusvelvollisille"
            />
          </Col>
        </Row>
      </Grid>
      <FormButtons buttons={buttons} />
    </Form>
  );
};

export default UsersForm;
